/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useMemo, useEffect, useCallback } from 'react';
// redux
import { useSelector, useDispatch } from 'react-redux';
// utils
import { validation } from '../../../../../../utils/validation';
import { url } from '../../../../../../utils/urls';
// material
import { Title, Form, Grid, GridSet, GridItem, Group, TextField, ImageUploader, log } from '@insticator/insticator-ui'; // iui-material
import ExpandableSection from "../../../../../../material/expandablesection/expandablesection";
import ConfigController from "../../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import SectionCard from "../../../../../../material/expandablesection/sectioncard/sectioncard";
// styles
import injectSheet from 'react-jss';
import styles from './roleform_styles';
import { Button } from '@insticator/insticator-ui/dist/material';

const formName = 'roledetailsform'
const defaultIconURL = `${url.static.url}/commenting/user-badge/default/Publisher.svg`
const defaultModeratorIcon = `${url.static.url}/commenting/user-badge/default/Moderator.svg`
const defaultEditorIcon = `${url.static.url}/commenting/user-badge/default/Editor.svg`
const defaultWriterIcon = `${url.static.url}/commenting/user-badge/default/Writer.svg`
const defaultProductManagerIcon = `${url.static.url}/commenting/user-badge/default/ProductManager.svg`

/* ========== ~~~~~~~~~~ ROLES FORM ~~~~~~~~~~ ========== */
const RoleForm = props => {

  const dispatch = useDispatch()

  // redux states
  const { accountCommentingRoles } = useSelector(state => state.account);
  const { accountUUID } = useSelector(state => state.session);

  const { title, data, isProcessing, onSave, onCancel } = props;
  const isEdit = !!data?.roleUUID || false;

  const [isOpened, setIsOpened] = useState(true);

    // flag for new user if form is submitted and if fields can be validated
  const [isNewRoleSubmitted, setIsNewRoleSubmitted] = useState(false)
  const [isEditRoleSubmitted, setIsEditRoleSubmitted] = useState(false)

  const [uploadImagePreview, setUploadImagePreview] = useState('');
  const [customImage, setCustomImage] = useState(null)

  const [isImageUploaderShown, showImageUploader] = useState(false)
  const [isDefaultIconShown, showDefaultIcon] = useState(false)

  // transpiled role data
  const [roleData, setRoleData] = useState({
    roleName: data?.roleName || '',
    roleUUID: data?.roleUUID || null,
    accountUUID: data?.accountUUID || null,
    badgeImageURL: data?.badgeImageURL || null,
    initialBadgeImageURL: data?.badgeImageURL || null,
    isDefault: data?.isDefault || false
  })
  // role fields
  const [roleFields, setRoleFields] = useState({
    roleName: data?.roleName || ''
  })
  // initial role fields values
  const [initialFieldsState, setInitialFieldsState] = useState({...roleFields})

  useEffect(() => {
    // set initial fields when passed role data got updated
    setInitialFieldsState({
      roleName: data?.roleName || ''
    })
  }, [data])

  const isValid = name => validation[name].test.test(roleFields[name])

  const isFormValid = useMemo(() => isValid(`roleName`), [roleFields])

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = () => {
    if(!isEdit) setIsNewRoleSubmitted(true)
    else setIsEditRoleSubmitted(true)

    if(isFormValid && !isDuplicateRoleName() && !isRoleNameContainSpaceOnly()) {
      let requestData = {
        roleName: roleFields.roleName,
        accountUUID
      }
      if(roleData.roleUUID) requestData.roleUUID = roleData.roleUUID;
      if(roleData.badgeImageURL && !customImage) requestData.badgeImageURL = roleData.badgeImageURL;
      if(customImage) requestData.customImage = customImage;
      log('SAVING ROLE', requestData)
      onSave(requestData)
    }
  }

  const handleCancel = () => {
    setRoleFields(initialFieldsState)
    if(onCancel) onCancel()
  }

  const handleFieldsChange = (e, fieldName) => setRoleFields({...roleFields, [fieldName]: e.target.value})

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(initialFieldsState) === JSON.stringify(roleFields));
    return isFieldsChanged
  }, [roleFields, initialFieldsState])

  const handleUpload = image => {
    setUploadImagePreview(URL.createObjectURL(image))
    setCustomImage(image)
  }

  const handleDiscard = () => {
    setUploadImagePreview(null)
    setCustomImage(null)
  }

  const handleUseDefaultIcon = () => {
    const badgeImageDefaultURL = currentRoleDefaultIcon !== defaultIconURL ? currentRoleDefaultIcon : null
    setRoleData({...roleData, badgeImageURL: badgeImageDefaultURL})
    showDefaultIcon(true)
  }

  const handleRevertChanges = () => {
    setRoleData({...roleData, badgeImageURL: roleData.initialBadgeImageURL})
    showDefaultIcon(false)
  }

  const handleDiscardImageChange = () => {
    setRoleData({...roleData, badgeImageURL: roleData.initialBadgeImageURL})
    showImageUploader(false)
    handleDiscard()
  }


  const currentRoleDefaultIcon = useMemo(() => {
    const formattedRole = roleData.roleName.toLowerCase()
    if(!roleData.isDefault) return defaultIconURL; // show the general default icon if role is not default
    switch(formattedRole) {
      case 'moderator':
        return defaultModeratorIcon;
      case 'editor':
        return defaultEditorIcon;
      case 'writer':
        return defaultWriterIcon;
      case 'product manager':
        return defaultProductManagerIcon;
      default:
        return defaultIconURL;
    }
  }, [roleData]);

  const isDuplicateRoleName = () => accountCommentingRoles.map(role => role.roleName).includes(roleFields.roleName) && roleFields.roleName !== roleData.roleName && !isRoleNameContainSpaceOnly()

  const isRoleNameFieldValid = () => (isEdit ? isValid(`roleName`) : (isNewRoleSubmitted ? isValid(`roleName`): true)) && !isDuplicateRoleName() && !isRoleNameContainSpaceOnly()

  const isRoleNameContainSpaceOnly = () => roleFields.roleName && roleFields.roleName.trim() === '' 

  const isRoleUsingDefaultIcon = useMemo(() => currentRoleDefaultIcon === roleData.initialBadgeImageURL, [roleData])

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isOpened={isOpened}
      isChanged={isChanged || !!uploadImagePreview || isDefaultIconShown}
      isProcessing={isProcessing}
      disableSave={!isFormValid}
      title={title}
    />
  );

  return (
    <ExpandableSection
      className={`roleform ${props.classes.roleform} ${!isEdit && 'newroleform'}`}
      controller={isEdit ? controller : null}
      isOpened={isOpened}
      isChanged={isEdit ? (isChanged || !!uploadImagePreview || isDefaultIconShown) : true}
      isProcessing={isProcessing}
      disableSave={isEdit ? !isFormValid : false}
      handleCancel={handleCancel}
      handleSave={handleSave}
    >
      {isOpened && <>
      <Form name={formName} editable={true} processing={isProcessing}>
        <SectionCard>
          {!isEdit && <Title klass={'roleformtitle'}><h2>New Role</h2></Title>}
            <Grid direction={`vertical`} name={`formcontainer`}>
                <GridSet direction={`horizontal`} name={'details'}>
                  <GridItem name={'rolenamegrid'}>
                    <TextField 
                      type={`text`} 
                      formName={formName} 
                      name={`roleName`} 
                      value={roleFields.roleName} 
                      handleChange={e => handleFieldsChange(e, `roleName`)} 
                      valid={isRoleNameFieldValid()} 
                      label={'Role Name'}
                      placeholder={`Enter commenting role name`} 
                      disabled={roleData.isDefault}
                    />
                    {!isRoleNameFieldValid() && !isDuplicateRoleName() && <div class={'errormessage'}>{validation[`roleName`].message}</div>}
                    {isDuplicateRoleName() && <div class={'errormessage'}>Duplicate role name</div>}
                  </GridItem>
                  <GridItem>
                    <label>Avatar</label>
                    <div className={`avatarfield ${!!uploadImagePreview ? 'show-upload-img' : ''}`}>
                    <Group name={`badgeimggrp show-${isEdit}`}>
                      <img src={currentRoleDefaultIcon} className={`badgeimg hidden-${!isDefaultIconShown}`} />
                      <img className={`badgeimg hidden-${!!uploadImagePreview || isImageUploaderShown || isDefaultIconShown}`} src={roleData.badgeImageURL} />
                    </Group>
                    { (!isEdit || isImageUploaderShown) &&
                    <Group name={`uploadimggrp`}> 
                      <ImageUploader
                        supportedFileTypes={["image/jpeg", "image/png"]}
                        maxSize={1000}
                        imageSrc={uploadImagePreview}
                        uploadImage={(value) => handleUpload(value)}
                        discardFile={handleDiscard}
                      />
                    </Group>
                    }
                    { isEdit &&
                      <Group name={`imgactiongrp`}>
                        {!isImageUploaderShown ? 
                        <Button variant={'primary'} text={'Change'} disabled={isDefaultIconShown} handleClick={() => showImageUploader(true)}  />
                        :
                        ( roleData.badgeImageURL && <Button variant={'system'} text={'Cancel'} handleClick={handleDiscardImageChange}  />)
                        }
                        { isDefaultIconShown ?
                          <Button variant={'system'} text={'Revert Changes'} handleClick={handleRevertChanges} />
                          :
                          (!isImageUploaderShown &&  <Button variant={'delete'} text={'Use Default Icon'} disabled={isImageUploaderShown || isRoleUsingDefaultIcon} handleClick={handleUseDefaultIcon} />)
                        }
                      </Group>
                    }
                    </div>
                  </GridItem>
               
                </GridSet>
            </Grid>
          </SectionCard>
        </Form>
      </>}
    </ExpandableSection>
  );
};

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(RoleForm);
